import { FilterChip } from "@/components/FilterChip";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import {
  api,
  USAGE_REPORT_PERIOD_OPTIONS as PERIOD_OPTIONS,
  USAGE_REPORT_PERIODS,
  type UsageReportPeriod,
} from "@/lib/api";
import { SpendAttributionSection } from "@/pages/container-tabs/SpendAttribution";
import { SpendChart } from "@/pages/container-tabs/SpendChart";
import { formatCostLong } from "@/lib/format";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";

// One Boo's cost view: a single Range picker, a total-spend number, the
// "Where spend goes" breakdown with the spend chart (SpendChart.tsx) under its cards. ONE period drives
// all of it — the ledger card and chart are cut to the same window the
// breakdown asks the brain for, so every number on the page describes the
// same days. The period set is the brain's (24h / 7d / 30d / all); the
// ledger reads take arbitrary bounds, so they follow.

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

function periodBounds(period: Exclude<UsageReportPeriod, "all">): { start: Date; end: Date } {
  const now = new Date();
  const span = period === "24h" ? DAY_MS : period === "7d" ? 7 * DAY_MS : 30 * DAY_MS;
  return { start: new Date(now.getTime() - span), end: now };
}

/**
 * `ledgerOnly`: just the credit-ledger spend, for someone with no role in the
 * Boo. The breakdowns come from inside the Boo, which they can't see.
 */
export function UsageTab({ ledgerOnly = false }: { ledgerOnly?: boolean }) {
  const params = useParams({ strict: false }) as { containerId?: string };
  const containerId = params.containerId!;
  const search: Record<string, unknown> = useSearch({ strict: false });
  const navigate = useNavigate();

  // The period lives in the URL (shared with the breakdown's filter state) so
  // Back/Forward step through range changes and a link carries its window.
  const periodRaw = search.uPeriod;
  const period: UsageReportPeriod = USAGE_REPORT_PERIODS.includes(periodRaw as UsageReportPeriod)
    ? (periodRaw as UsageReportPeriod)
    : "30d";
  // Fire-and-forget: the router resolves the search patch synchronously in
  // practice; `void` just settles the floating-promise lint.
  const setPeriod = (next: UsageReportPeriod) =>
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const out: Record<string, unknown> = { ...prev };
        if (next === "30d") delete out.uPeriod;
        else out.uPeriod = next;
        return out;
      },
    });

  const allTime = period === "all";
  // Null for All time: the ledger read is monthly over the Boo's whole life.
  const bounds = useMemo(() => (allTime ? null : periodBounds(period)), [period, allTime]);
  const byDay = useQuery({
    queryKey: ["container-usage-by-day", containerId, period],
    queryFn: () =>
      bounds === null
        ? api.containers.usage.byMonthAllTime(containerId)
        : api.containers.usage.byDay(containerId, bounds.start.toISOString(), bounds.end.toISOString()),
  });
  const days = useMemo(() => byDay.data?.days ?? [], [byDay.data]);
  // The ledger's own split of the window. The breakdown below anchors to it:
  // the people / channels / activity cards each add up to `ai` (model
  // charges), a fourth card carries the rest, and the two totals make
  // `total`.
  const totals = useMemo(
    () =>
      days.reduce(
        (acc, d) => ({
          total: acc.total + d.total,
          ai: acc.ai + d.ai,
          infrastructure: acc.infrastructure + d.infrastructure,
          services: acc.services + d.services,
          base: acc.base + d.base,
        }),
        { total: 0, ai: 0, infrastructure: 0, services: 0, base: 0 },
      ),
    [days],
  );

  // Sits between the breakdown cards and the conversations (the breakdown
  // places it), or right under Total spend when there's no breakdown.
  const chart = (
    <SpendChart
      containerId={containerId}
      period={period}
      ledgerOnly={ledgerOnly}
      days={days}
      bounds={bounds}
      ledger={totals}
    />
  );

  if (byDay.isLoading) return <LoadingIndicator />;
  if (byDay.isError || !byDay.data) {
    return <ErrorComponent error={byDay.error ?? "Failed to load usage"} />;
  }

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-auto p-6 gap-6">
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip
          label="Range"
          value={PERIOD_OPTIONS.find((p) => p.key === period)?.label ?? period}
          options={PERIOD_OPTIONS}
          onSelect={(k) => setPeriod(k as UsageReportPeriod)}
        />
      </div>

      <div className="flex flex-col gap-1 rounded-lg border p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Total spend</div>
        <div className="text-3xl font-semibold tracking-tight">{formatCostLong(totals.total)}</div>
        <div className="text-xs text-muted-foreground">
          Everything deducted from your credit balance for this Boo in this range.
        </div>
      </div>

      {ledgerOnly ? (
        chart
      ) : (
        <SpendAttributionSection
          containerId={containerId}
          period={period}
          ledger={totals}
          chart={chart}
        />
      )}
    </div>
  );
}
